/**
 * PROJ-060: Session Geolocation Info Component
 *
 * Displays IP address and country flag for current session
 * Used in AppFooter
 */

import { useCurrentSession } from '@/hooks/useCurrentSession';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MapPin, Globe } from 'lucide-react';

export function SessionGeoInfo() {
  const { data: session, isLoading } = useCurrentSession();

  // Don't show anything while loading or if no session data
  if (isLoading) {
    return (
      <span className="flex items-center gap-1 opacity-50">
        <Globe className="h-3 w-3 animate-pulse" />
        <span className="text-[10px]">...</span>
      </span>
    );
  }

  if (!session || !session.countryCode) {
    return null;
  }

  const displayIP = session.ipMasked || session.ipAddress || '?.?.?.?';
  const countryEmoji = session.countryEmoji || '🌍';
  const countryName = session.countryName || session.countryCode || 'Unknown';
  const city = session.city;
  const region = session.regionCode;

  // Build location string
  const locationParts = [city, region, countryName].filter(Boolean);
  const locationString = locationParts.join(', ');

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex items-center gap-1.5 cursor-default select-none">
            <span
              className="text-base leading-none"
              role="img"
              aria-label={countryName}
            >
              {countryEmoji}
            </span>
            <span className="text-[11px] font-mono text-muted-foreground/80 hidden sm:inline">
              {displayIP}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3" />
              <span>{locationString}</span>
            </div>
            <div className="text-muted-foreground">
              IP: {displayIP}
            </div>
            {session.browser && session.os && (
              <div className="text-muted-foreground">
                {session.browser} on {session.os}
              </div>
            )}
            {session.isEu && (
              <div className="text-blue-500 text-[10px]">
                EU Region (GDPR)
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
